// 🔥 통합 모델/가격 수정 다이얼로그 - 최고급 구현 (용량 관리 기능 추가)
// [배경] 관리자가 모델 정보(이름, 시리즈, 연결성)와 가격을 한곳에서 수정
//        + 🆕 기존 모델에 새 용량(Variant) 추가/삭제 기능 포함
//        UX 단순화를 위해 분산된 편집 기능을 하나로 통합

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  OutlinedInput,
  SelectChangeEvent,
  Alert,
  Typography,
  Divider,
  InputAdornment,
  Paper,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ExpandMore as ExpandMoreIcon,
  Storage as StorageIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { 
  DeviceCatalogDocument, 
  DeviceBrand, 
  DeviceType,
  generatePriceId,
  ConnectivityType
} from '../../../../types/deviceCatalog';
import { DeviceGrade, GradePriceInfo } from '../../../../types/gradeSystem';
import { DeviceCatalogService } from '../../../../services/deviceCatalogService';
import { getUnifiedPriceService } from '../../../../services/unifiedPriceAdapter';
import { doc, updateDoc, collection, query, where, getDocs, addDoc, serverTimestamp, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { generateModelId } from '../../../../types/deviceCatalog';

interface EditModelDialogProps {
  open: boolean;
  onClose: () => void;
  model: DeviceCatalogDocument | null;
  initialPrices?: GradePriceInfo; // 초기 가격 정보 (선택)
  targetConfig?: { // 특정 구성 편집 시 (선택)
    storage: string;
    connectivity: string;
    priceId: string;
  };
  onSuccess: () => void;
  brand: DeviceBrand;
  deviceType: DeviceType;
}

// 🆕 새로 추가할 용량 정보 인터페이스
interface NewStorageItem {
  storage: string;
  initialPrices: GradePriceInfo;
}

const EditModelDialog: React.FC<EditModelDialogProps> = ({
  open,
  onClose,
  model,
  initialPrices,
  targetConfig,
  onSuccess,
  brand,
  deviceType
}) => {
  // 📝 폼 상태
  const [formData, setFormData] = useState({
    modelKo: '',
    modelEn: '',
    series: '',
    brand: brand, // 🆕 제조사 필드 추가
    connectivityOptions: [] as string[],
    storageOptions: [] as string[], // 🆕 기존 용량 옵션들
    prices: { A: 0, B: 0, C: 0, D: 0, E: 0 } as GradePriceInfo
  });
  
  // 🆕 새로 추가할 용량 상태
  const [newStorages, setNewStorages] = useState<NewStorageItem[]>([]);
  const [newStorageInput, setNewStorageInput] = useState('');
  const [newStoragePrices, setNewStoragePrices] = useState<GradePriceInfo>({ A: 0, B: 0, C: 0, D: 0, E: 0 });
  
  // 🆕 삭제 예정 용량 상태
  const [storagesToDelete, setStoragesToDelete] = useState<string[]>([]);
  
  // 🆕 용량 관리 섹션 확장 상태 (기본: 열림)
  const [storageAccordionExpanded, setStorageAccordionExpanded] = useState(true);

  // 🆕 제조사 변경 확인 모달
  const [brandChangeConfirmOpen, setBrandChangeConfirmOpen] = useState(false);
  const [pendingBrand, setPendingBrand] = useState<DeviceBrand | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<string | null>(null);

  // 초기 데이터 로드
  useEffect(() => {
    if (model && open) {
      // 🔧 disabledVariants에 있는 용량은 storageOptions에서 제외 (이미 비활성화된 것들)
      // 테이블에서 삭제된 용량은 disabledVariants에 저장되므로, 
      // "현재 등록된 용량"에는 활성 용량만 표시해야 함
      const disabledVariants: { storage: string; connectivity?: string }[] = 
        (model as any).disabledVariants || [];
      const disabledStorages = disabledVariants.map(v => v.storage);
      const activeStorageOptions = (model.storageOptions || []).filter(
        storage => !disabledStorages.includes(storage)
      );

      console.log(`🔧 EditModelDialog 초기화:`, {
        전체용량: model.storageOptions,
        비활성용량: disabledStorages,
        활성용량: activeStorageOptions
      });

      setFormData({
        modelKo: model.modelKo || '',
        modelEn: model.modelEn || '',
        series: model.series || '',
        brand: model.brand || brand, // 🆕 모델의 현재 제조사
        connectivityOptions: model.connectivityOptions || [],
        storageOptions: activeStorageOptions, // 🔧 활성 용량만 표시
        prices: initialPrices || { A: 0, B: 0, C: 0, D: 0, E: 0 }
      });
      // 상태 초기화
      setNewStorages([]);
      setNewStorageInput('');
      setNewStoragePrices({ A: 0, B: 0, C: 0, D: 0, E: 0 });
      setStoragesToDelete([]);
      setError(null);
      setSuccessInfo(null);
      // 🔧 용량 관리 섹션은 기본으로 열어둠 (웨어러블 제외)
      setStorageAccordionExpanded(true);
    }
  }, [model, initialPrices, open]);

  // 🎯 시리즈 옵션 (tablet, wearable, smartphone 지원)
  const getSeriesOptions = () => {
    const currentBrand = formData.brand; // 🆕 현재 선택된 제조사 사용

    if (deviceType === 'tablet') {
      if (currentBrand === 'Apple') return ['iPad Pro', 'iPad Air', 'iPad mini', 'iPad'];
      if (currentBrand === 'Samsung') return ['Galaxy Tab S', 'Galaxy Tab A', 'Galaxy Tab Active', 'Galaxy Tab Pro'];
      if (currentBrand === 'LG') return ['G Pad', 'V Pad'];
    } else if (deviceType === 'wearable') {
      if (currentBrand === 'Apple') return ['AirPods', 'AirPods Pro', 'AirPods Max'];
      if (currentBrand === 'Samsung') return ['Galaxy Buds', 'Galaxy Buds Pro', 'Galaxy Watch'];
      if (currentBrand === 'LG') return ['LG Tone'];
    } else {
      if (currentBrand === 'Apple') {
        return ['iPhone 17', 'iPhone 16', 'iPhone 15', 'iPhone 14', 'iPhone SE', 'iPhone 13', 'iPhone 12', 'iPhone 11',
                'iPhone XS', 'iPhone XR', 'iPhone X', 'iPhone 8', 'iPhone 7', 'iPhone 6'];
      }
      if (currentBrand === 'Samsung') {
        return ['Galaxy S', 'Galaxy Z Flip', 'Galaxy Z Fold', 'Galaxy Note', 'Galaxy A'];
      }
      if (currentBrand === 'LG') {
        return ['LG V', 'LG G', 'LG Q', 'LG Wing', 'LG Velvet'];
      }
    }
    return [];
  };

  // 🎯 연결성 옵션
  const getConnectivityOptions = () => {
    if (deviceType === 'tablet') {
      return ['Wi-Fi', 'Wi-Fi + Cellular', 'Wi-Fi + 5G'];
    }
    if (deviceType === 'wearable') {
      return ['기본'];
    }
    return ['기본'];
  };

  // 🎯 기본 스토리지 옵션 (추가 시 선택지)
  const getDefaultStorageOptions = () => {
    if (deviceType === 'wearable') {
      return ['풀세트', '유닛 세트', '유닛 1쪽', '본체만'];
    }
    return ['32GB', '64GB', '128GB', '256GB', '512GB', '1TB', '2TB'];
  };

  // 등급 명칭 반환
  const getGradeLabel = (grade: DeviceGrade): string => {
    return `${grade}등급`;
  };

  // 🔍 중복 검사
  const checkDuplicate = async (newModelKo: string, newSeries: string): Promise<boolean> => {
    if (!model) return false;
    if (newModelKo === model.modelKo && newSeries === model.series) return false;

    try {
      const q = query(
        collection(db, 'device_catalog'),
        where('brand', '==', brand),
        where('deviceType', '==', deviceType),
        where('modelKo', '==', newModelKo),
        where('series', '==', newSeries)
      );
      const snapshot = await getDocs(q);
      const duplicates = snapshot.docs.filter(d => d.id !== model.id);
      return duplicates.length > 0;
    } catch (err) {
      throw new Error("데이터베이스 확인 중 오류가 발생했습니다.");
    }
  };

  // 🆕 새 용량 추가 핸들러
  const handleAddNewStorage = () => {
    const trimmedStorage = newStorageInput.trim();
    
    if (!trimmedStorage) {
      setError('용량을 입력해주세요.');
      return;
    }
    
    // 중복 체크 (기존 + 새로 추가된 것 모두)
    const allStorages = [
      ...formData.storageOptions,
      ...newStorages.map(ns => ns.storage)
    ];
    
    if (allStorages.includes(trimmedStorage)) {
      setError(`"${trimmedStorage}" 용량은 이미 존재합니다.`);
      return;
    }
    
    // 삭제 예정 목록에서 제거 (복원 효과)
    if (storagesToDelete.includes(trimmedStorage)) {
      setStoragesToDelete(prev => prev.filter(s => s !== trimmedStorage));
      setNewStorageInput('');
      setSuccessInfo(`"${trimmedStorage}" 용량이 복원되었습니다.`);
      return;
    }
    
    // 새 용량 추가
    setNewStorages(prev => [...prev, {
      storage: trimmedStorage,
      initialPrices: { ...newStoragePrices }
    }]);
    
    // 입력 초기화
    setNewStorageInput('');
    setNewStoragePrices({ A: 0, B: 0, C: 0, D: 0, E: 0 });
    setError(null);
    setSuccessInfo(`"${trimmedStorage}" 용량이 추가 대기 목록에 등록되었습니다.`);
  };

  // 🆕 새 용량 제거 (추가 대기 목록에서)
  const handleRemoveNewStorage = (storage: string) => {
    setNewStorages(prev => prev.filter(ns => ns.storage !== storage));
  };

  // 🆕 기존 용량 삭제 예정 토글
  const handleToggleDeleteStorage = (storage: string) => {
    if (storagesToDelete.includes(storage)) {
      // 삭제 취소 (복원)
      setStoragesToDelete(prev => prev.filter(s => s !== storage));
    } else {
      // 삭제 예정에 추가
      setStoragesToDelete(prev => [...prev, storage]);
    }
  };

  // 🆕 새 용량 가격 변경 핸들러
  const handleNewStoragePriceChange = (grade: DeviceGrade, value: string) => {
    const numValue = parseInt(value.replace(/[^0-9]/g, '')) || 0;
    setNewStoragePrices(prev => ({ ...prev, [grade]: numValue }));
  };

  // 📊 변경 사항 요약
  const changesSummary = useMemo(() => {
    const changes: string[] = [];

    if (model) {
      if (model.brand !== formData.brand) changes.push(`⚠️ 제조사 변경: ${model.brand} → ${formData.brand}`); // 🆕 제조사 변경
      if (model.modelKo !== formData.modelKo) changes.push(`모델명 변경: ${model.modelKo} → ${formData.modelKo}`);
      if (model.series !== formData.series) changes.push(`시리즈 변경: ${model.series} → ${formData.series}`);
      if (JSON.stringify(model.connectivityOptions) !== JSON.stringify(formData.connectivityOptions)) {
        changes.push(`연결성 옵션 변경`);
      }
    }
    
    if (newStorages.length > 0) {
      changes.push(`${newStorages.length}개 용량 추가: ${newStorages.map(ns => ns.storage).join(', ')}`);
    }
    
    if (storagesToDelete.length > 0) {
      changes.push(`${storagesToDelete.length}개 용량 삭제: ${storagesToDelete.join(', ')}`);
    }
    
    if (targetConfig) {
      changes.push(`가격 수정: ${targetConfig.storage} ${targetConfig.connectivity}`);
    }
    
    return changes;
  }, [model, formData, newStorages, storagesToDelete, targetConfig]);

  // 💾 통합 저장 핸들러
  const handleSave = async () => {
    if (!model) return;
    
    // 유효성 검사
    if (!formData.modelKo.trim()) { setError("모델명(한글)을 입력해주세요."); return; }
    if (!formData.series) { setError("시리즈를 선택해주세요."); return; }
    if (formData.connectivityOptions.length === 0) { setError("최소 1개의 연결성 옵션을 선택해주세요."); return; }

    // 용량이 모두 삭제되는 경우 방지
    const remainingStorages = formData.storageOptions.filter(s => !storagesToDelete.includes(s));
    const totalStorages = remainingStorages.length + newStorages.length;
    if (totalStorages === 0) {
      setError("최소 1개의 용량 옵션이 필요합니다.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const promises: Promise<any>[] = [];
      const UnifiedPriceService = getUnifiedPriceService();

      // 1. 모델 정보 변경 확인
      const isModelInfoChanged =
        model.modelKo !== formData.modelKo ||
        model.series !== formData.series ||
        model.modelEn !== formData.modelEn ||
        model.brand !== formData.brand || // 🆕 제조사 변경 확인
        JSON.stringify(model.connectivityOptions) !== JSON.stringify(formData.connectivityOptions);

      // 2. storageOptions 변경 확인
      const newStorageOptionsArray = [
        ...formData.storageOptions.filter(s => !storagesToDelete.includes(s)),
        ...newStorages.map(ns => ns.storage)
      ];
      const isStorageChanged = 
        JSON.stringify(model.storageOptions.sort()) !== JSON.stringify(newStorageOptionsArray.sort()) ||
        storagesToDelete.length > 0;

      // 3. 중복 검사 (이름이나 시리즈가 변경된 경우에만)
      if (isModelInfoChanged && (model.modelKo !== formData.modelKo || model.series !== formData.series)) {
        const isDuplicate = await checkDuplicate(formData.modelKo, formData.series);
        if (isDuplicate) {
          setError(`이미 존재하는 모델입니다: [${formData.series}] ${formData.modelKo}`);
          setLoading(false);
          return;
        }
      }

      // 🆕 3-1. 제조사 변경 시 모델 재생성 (삭제 + 생성)
      const isBrandChanged = model.brand !== formData.brand;
      let newModelId = model.id; // 기본값은 기존 ID

      if (isBrandChanged) {
        console.log(`🔄 제조사 변경 감지: ${model.brand} → ${formData.brand}`);
        console.log(`🔥 모델 재생성 프로세스 시작`);

        // 1. 새로운 modelId 생성
        newModelId = generateModelId({
          brand: formData.brand,
          deviceType: model.deviceType,
          series: formData.series,
          modelEn: formData.modelEn
        });

        console.log(`📌 새 modelId 생성: ${model.id} → ${newModelId}`);

        // 2. 기존 가격 문서 조회
        const oldPricesQuery = query(
          collection(db, 'unified_prices'),
          where('modelId', '==', model.id)
        );
        const oldPricesSnapshot = await getDocs(oldPricesQuery);
        console.log(`📊 복사할 가격 문서 수: ${oldPricesSnapshot.docs.length}`);

        // 3. 새 모델 문서 생성 (setDoc으로 덮어쓰기)
        const newModelRef = doc(db, 'device_catalog', newModelId);
        const newModelData = {
          ...model,
          id: newModelId,
          brand: formData.brand,
          modelKo: formData.modelKo,
          modelEn: formData.modelEn,
          series: formData.series,
          connectivityOptions: formData.connectivityOptions,
          storageOptions: newStorageOptionsArray,
          updatedAt: new Date(),
          updatedBy: 'admin',
          createdAt: model.createdAt || new Date() // 원본 생성일 유지
        };

        // disabledVariants 처리 (기존 로직 재사용)
        const existingDisabledVariants: { storage: string; connectivity?: ConnectivityType }[] =
          (model as any).disabledVariants || [];
        const addedStorages = newStorages.map(ns => ns.storage);
        const filteredDisabledVariants = existingDisabledVariants.filter(
          (v) => !addedStorages.includes(v.storage)
        );
        const storagesToActuallyDelete = storagesToDelete.filter(s => !addedStorages.includes(s));
        const newDisabledVariants = [
          ...filteredDisabledVariants,
          ...storagesToActuallyDelete.flatMap(storage =>
            formData.connectivityOptions.map(connectivity => ({
              storage,
              connectivity: connectivity as ConnectivityType
            }))
          )
        ];
        newModelData.disabledVariants = newDisabledVariants;

        promises.push(setDoc(newModelRef, newModelData));
        console.log(`✅ 새 모델 문서 생성 예정: ${newModelId}`);

        // 4. 가격 문서 복사 (새 modelId와 새 priceId로)
        for (const oldPriceDoc of oldPricesSnapshot.docs) {
          const oldPriceData = oldPriceDoc.data();

          // 새 priceId 생성
          const newPriceId = generatePriceId(
            newModelId,
            {
              storage: oldPriceData.variant?.storage || '기본',
              connectivity: oldPriceData.variant?.connectivity
            },
            'KR'
          );

          const newPriceRef = doc(db, 'unified_prices', newPriceId);
          const newPriceData = {
            ...oldPriceData,
            id: newPriceId,
            modelId: newModelId,
            brand: formData.brand,
            deviceType: model.deviceType, // ✅ 명시적으로 deviceType 설정
            updatedAt: new Date(),
            updatedBy: 'admin',
            memo: `제조사 변경으로 인한 재생성: ${model.brand} → ${formData.brand} - ${new Date().toLocaleString()}`,
            legacy: {
              originalModelId: model.id,
              originalPriceId: oldPriceDoc.id,
              migratedAt: new Date(),
              reason: 'Brand change migration'
            }
          };

          promises.push(setDoc(newPriceRef, newPriceData));
          console.log(`💰 가격 문서 복사: ${oldPriceDoc.id} → ${newPriceId}`);
        }

        // 4-1. 🆕 새로 추가된 용량에 대한 가격 문서 생성 (제조사 변경 시)
        if (newStorages.length > 0) {
          console.log(`📦 제조사 변경과 함께 ${newStorages.length}개 신규 용량 추가`);

          for (const newStorage of newStorages) {
            for (const connectivity of formData.connectivityOptions) {
              // priceId 생성
              const effectiveConnectivity = (deviceType === 'wearable' || connectivity === 'Wi-Fi' || connectivity === '기본')
                ? undefined
                : connectivity;

              const newPriceId = generatePriceId(
                newModelId, // 새 modelId 사용
                {
                  storage: newStorage.storage,
                  connectivity: effectiveConnectivity
                },
                'KR'
              );

              console.log(`💰 신규 용량 가격 문서 생성 예정: ${newPriceId}`);
              console.log(`  - 모델: ${formData.modelKo}`);
              console.log(`  - 용량: ${newStorage.storage}`);
              console.log(`  - 연결성: ${connectivity}`);
              console.log(`  - 가격: A=${newStorage.initialPrices.A} B=${newStorage.initialPrices.B} C=${newStorage.initialPrices.C} D=${newStorage.initialPrices.D}`);

              // ✅ setDoc으로 완전한 문서 생성 (updatePrice는 기존 문서만 수정 가능)
              const newPriceDocRef = doc(db, 'unified_prices', newPriceId);
              const newPriceDocData = {
                id: newPriceId,
                modelId: newModelId,
                deviceType: model.deviceType, // ✅ 명시적으로 deviceType 설정
                brand: formData.brand,
                variant: {
                  storage: newStorage.storage,
                  connectivity: effectiveConnectivity
                },
                region: 'KR' as const,
                currency: 'KRW' as const,
                gradePrices: newStorage.initialPrices,
                isActive: true,
                version: 'live' as const,
                createdAt: new Date(),
                updatedAt: new Date(),
                updatedBy: 'admin',
                memo: `새 용량 추가 (제조사 변경 시: ${newStorage.storage}) - ${new Date().toLocaleString()}`
              };

              promises.push(setDoc(newPriceDocRef, newPriceDocData));
            }
          }
        }

        // 5. 기존 가격 문서 삭제
        for (const oldPriceDoc of oldPricesSnapshot.docs) {
          const oldPriceRef = doc(db, 'unified_prices', oldPriceDoc.id);
          promises.push(deleteDoc(oldPriceRef));
          console.log(`🗑️ 기존 가격 문서 삭제 예정: ${oldPriceDoc.id}`);
        }

        // 6. 기존 모델 문서 삭제
        const oldModelRef = doc(db, 'device_catalog', model.id);
        promises.push(deleteDoc(oldModelRef));
        console.log(`🗑️ 기존 모델 문서 삭제 예정: ${model.id}`);

        // 7. 제조사 변경 로그 기록
        promises.push(
          addDoc(collection(db, 'brand_change_logs'), {
            oldModelId: model.id,
            newModelId: newModelId,
            modelKo: model.modelKo,
            modelEn: model.modelEn,
            deviceType: model.deviceType,
            oldBrand: model.brand,
            newBrand: formData.brand,
            affectedPriceDocuments: oldPricesSnapshot.docs.length,
            migrationType: 'DELETE_AND_RECREATE',
            changedBy: 'admin',
            changedAt: serverTimestamp(),
            reason: 'Brand change with model recreation'
          })
        );

        console.log(`📝 제조사 변경 로그 기록 예정: ${model.brand} → ${formData.brand}`);
        console.log(`🔥 모델 재생성 프로세스 완료: ${oldPricesSnapshot.docs.length}개 가격 문서 이전`);
      }

      // 4. 모델 문서 업데이트 (정보 변경 또는 용량 변경 시)
      // 단, 제조사 변경 시에는 이미 새 모델을 생성했으므로 스킵
      if (!isBrandChanged && (isModelInfoChanged || isStorageChanged || newStorages.length > 0)) {
        // 🔧 disabledVariants 업데이트 로직 개선
        // - 용량 삭제 시: disabledVariants에 추가
        // - 용량 추가 시: disabledVariants에서 제거 (복원 효과)
        const existingDisabledVariants: { storage: string; connectivity?: ConnectivityType }[] = 
          (model as any).disabledVariants || [];
        const addedStorages = newStorages.map(ns => ns.storage);

        // 🔧 Step 1: 새로 추가되는 용량은 disabledVariants에서 제거 (복원 효과)
        const filteredDisabledVariants = existingDisabledVariants.filter(
          (v) => !addedStorages.includes(v.storage)
        );

        // 🔧 Step 2: 삭제 예정 용량 추가 (단, 새로 추가되는 용량은 제외 - 삭제 후 재추가 시나리오)
        const storagesToActuallyDelete = storagesToDelete.filter(s => !addedStorages.includes(s));
        const newDisabledVariants = [
          ...filteredDisabledVariants,
          ...storagesToActuallyDelete.flatMap(storage => 
            formData.connectivityOptions.map(connectivity => ({
              storage,
              connectivity: connectivity as ConnectivityType
            }))
          )
        ];

        console.log(`🔧 disabledVariants 업데이트 로직:`, {
          기존비활성: existingDisabledVariants.map(v => v.storage),
          새로추가: addedStorages,
          필터링후: filteredDisabledVariants.map(v => v.storage),
          실제삭제: storagesToActuallyDelete,
          최종결과: newDisabledVariants.map(v => v.storage)
        });

        const modelRef = doc(db, 'device_catalog', model.id);
        promises.push(
          updateDoc(modelRef, {
            modelKo: formData.modelKo,
            modelEn: formData.modelEn,
            series: formData.series,
            brand: formData.brand, // 🆕 제조사 업데이트
            connectivityOptions: formData.connectivityOptions,
            storageOptions: newStorageOptionsArray,
            disabledVariants: newDisabledVariants,
            updatedAt: new Date(),
            updatedBy: 'admin'
          })
        );

        console.log(`📝 모델 문서 업데이트 예정:`, {
          storageOptions: newStorageOptionsArray,
          disabledVariants: newDisabledVariants
        });
      }

      // 5. 🆕 새 용량에 대한 가격 문서 생성 (제조사 변경 없을 때만)
      // 제조사 변경 시에는 위 섹션(4-1)에서 이미 처리됨
      if (newStorages.length > 0 && !isBrandChanged) {
        for (const newStorage of newStorages) {
          for (const connectivity of formData.connectivityOptions) {
            // priceId 생성
            const effectiveConnectivity = (deviceType === 'wearable' || connectivity === 'Wi-Fi' || connectivity === '기본')
              ? undefined
              : connectivity;

            const priceId = generatePriceId(
              model.id, // 기존 modelId 사용 (제조사 변경 없음)
              {
                storage: newStorage.storage,
                connectivity: effectiveConnectivity
              },
              'KR'
            );

            console.log(`💰 새 가격 문서 생성 예정: ${priceId}`);
            console.log(`  - 모델: ${model.modelKo}`);
            console.log(`  - 용량: ${newStorage.storage}`);
            console.log(`  - 연결성: ${connectivity}`);
            console.log(`  - 가격: A=${newStorage.initialPrices.A} B=${newStorage.initialPrices.B} C=${newStorage.initialPrices.C} D=${newStorage.initialPrices.D}`);

            // ✅ setDoc으로 완전한 문서 생성 (updatePrice는 기존 문서만 수정 가능)
            const newPriceDocRef = doc(db, 'unified_prices', priceId);
            const newPriceDocData = {
              id: priceId,
              modelId: model.id,
              deviceType: model.deviceType, // ✅ 명시적으로 deviceType 설정
              brand: formData.brand,
              variant: {
                storage: newStorage.storage,
                connectivity: effectiveConnectivity
              },
              region: 'KR' as const,
              currency: 'KRW' as const,
              gradePrices: newStorage.initialPrices,
              isActive: true,
              version: 'live' as const,
              createdAt: new Date(),
              updatedAt: new Date(),
              updatedBy: 'admin',
              memo: `새 용량 추가 (${newStorage.storage}) - ${new Date().toLocaleString()}`
            };

            promises.push(setDoc(newPriceDocRef, newPriceDocData));
          }
        }
      }

      // 6. 기존 가격 정보 업데이트 (타겟 구성이 있는 경우)
      if (targetConfig) {
        // 제조사 변경 시 새로운 priceId를 계산해야 함
        const effectivePriceId = isBrandChanged
          ? generatePriceId(
              newModelId,
              {
                storage: targetConfig.storage === '기본' ? '기본' : targetConfig.storage,
                connectivity: targetConfig.connectivity
              },
              'KR'
            )
          : targetConfig.priceId;

        promises.push(
          UnifiedPriceService.updatePrice({
            priceId: effectivePriceId,
            gradePrices: formData.prices,
            memo: isBrandChanged
              ? `제조사 변경 시 가격 수정 (${targetConfig.storage}/${targetConfig.connectivity || '기본'}) - ${new Date().toLocaleString()}`
              : `관리자 개별 수정 (${targetConfig.storage}/${targetConfig.connectivity || '기본'}) - ${new Date().toLocaleString()}`
          }, 'admin')
        );

        console.log(`💰 타겟 가격 업데이트: ${effectivePriceId}${isBrandChanged ? ' (제조사 변경으로 새 ID)' : ''}`);
      }

      // 7. 모든 변경사항 실행
      await Promise.all(promises);

      console.log(`✅ 저장 완료:`, {
        modelInfoChanged: isModelInfoChanged,
        storageChanged: isStorageChanged,
        newStoragesAdded: newStorages.length,
        storagesDeleted: storagesToDelete.length,
        priceUpdated: !!targetConfig,
        brandChanged: isBrandChanged
      });

      // 8. 캐시 정리 및 성공 처리
      DeviceCatalogService.clearCache();
      UnifiedPriceService.clearCache();

      // 🆕 제조사가 변경되었으면 사용자에게 알림
      if (isBrandChanged) {
        const oldBrand = model.brand;
        const newBrand = formData.brand;
        const deviceTypeKo = deviceType === 'tablet' ? '태블릿' : deviceType === 'smartphone' ? '스마트폰' : '웨어러블';

        alert(
          `✅ "${model.modelKo}" 모델의 제조사 변경 완료!\n\n` +
          `변경 방식: 완전 삭제 + 재생성\n` +
          `• 이전 제조사: ${oldBrand}\n` +
          `• 새 제조사: ${newBrand}\n` +
          `• 이전 모델ID: ${model.id}\n` +
          `• 새 모델ID: ${newModelId}\n\n` +
          `📌 처리 완료 내역:\n` +
          `1. 기존 모델 및 가격 데이터 삭제됨\n` +
          `2. 새 모델ID로 모델 재생성됨\n` +
          `3. 모든 가격 데이터가 새 ID로 복사됨${newStorages.length > 0 ? `\n4. 신규 용량 ${newStorages.length}개 추가됨` : ''}\n\n` +
          `📌 중요 안내:\n` +
          `• 이 모델은 이제 "${newBrand} ${deviceTypeKo}" 페이지에 표시됩니다.\n` +
          `• 현재 페이지를 새로고침(F5)하여 최신 데이터를 확인하세요.\n` +
          `• "${newBrand} ${deviceTypeKo}" 메뉴로 이동하면 변경된 모델을 확인할 수 있습니다.`
        );
      }

      onSuccess();
      onClose();

    } catch (err) {
      console.error("❌ 수정 실패:", err);
      setError(err instanceof Error ? err.message : "수정 중 알 수 없는 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (grade: DeviceGrade, value: string) => {
    const numValue = parseInt(value.replace(/[^0-9]/g, '')) || 0;
    setFormData(prev => ({
      ...prev,
      prices: {
        ...prev.prices,
        [grade]: numValue
      }
    }));
  };

  const handleConnectivityChange = (event: SelectChangeEvent<string[]>) => {
    const { target: { value } } = event;
    setFormData(prev => ({
      ...prev,
      connectivityOptions: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  // 🆕 제조사 변경 핸들러
  const handleBrandChange = (newBrand: DeviceBrand) => {
    if (!model) return;

    // 제조사가 실제로 변경되는 경우에만 확인 모달 표시
    if (model.brand !== newBrand) {
      setPendingBrand(newBrand);
      setBrandChangeConfirmOpen(true);
    }
  };

  // 🆕 제조사 변경 확인
  const handleConfirmBrandChange = () => {
    if (pendingBrand) {
      setFormData(prev => ({ ...prev, brand: pendingBrand }));
      setSuccessInfo(`제조사가 "${pendingBrand}"로 변경됩니다. 저장 시 적용됩니다.`);
    }
    setBrandChangeConfirmOpen(false);
    setPendingBrand(null);
  };

  // 🆕 제조사 변경 취소
  const handleCancelBrandChange = () => {
    setBrandChangeConfirmOpen(false);
    setPendingBrand(null);
  };

  return (
    <>
    {/* 🆕 제조사 변경 확인 모달 */}
    <Dialog open={brandChangeConfirmOpen} onClose={handleCancelBrandChange} maxWidth="sm">
      <DialogTitle sx={{ bgcolor: '#fff3cd', color: '#856404' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6" fontWeight="bold">
            제조사 변경 확인
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            제조사를 변경하면 다음과 같은 영향이 있습니다:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>이 모델이 "{model?.brand}" 카테고리에서 "{pendingBrand}" 카테고리로 이동됩니다.</li>
            <li>모델 ID는 변경되지 않지만, brand 필드만 업데이트됩니다.</li>
            <li>가격 데이터는 modelId로 연결되어 있어 자동으로 함께 이동됩니다.</li>
            <li>이 작업은 되돌릴 수 있습니다 (다시 제조사를 변경하면 됩니다).</li>
          </Box>
        </Alert>
        <Typography variant="body1" sx={{ mt: 2 }}>
          <strong>{model?.modelKo}</strong> 모델의 제조사를{' '}
          <strong style={{ color: '#d32f2f' }}>{model?.brand}</strong>에서{' '}
          <strong style={{ color: '#2e7d32' }}>{pendingBrand}</strong>로 변경하시겠습니까?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleCancelBrandChange} color="inherit">
          취소
        </Button>
        <Button onClick={handleConfirmBrandChange} variant="contained" color="warning" startIcon={<CheckIcon />}>
          변경 확인
        </Button>
      </DialogActions>
    </Dialog>

    {/* 메인 편집 모달 */}
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorageIcon color="primary" />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {targetConfig ? '모델 및 가격 수정' : '모델 정보 수정'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {model?.modelKo} {targetConfig && `(${targetConfig.storage}, ${targetConfig.connectivity})`}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ maxHeight: '70vh' }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {successInfo && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessInfo(null)}>{successInfo}</Alert>}

        <Grid container spacing={3}>
          {/* 섹션 1: 모델 기본 정보 */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              📱 모델 정보 (모든 구성에 공통 적용)
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>제조사</InputLabel>
                    <Select
                      value={formData.brand}
                      label="제조사"
                      onChange={(e) => handleBrandChange(e.target.value as DeviceBrand)}
                    >
                      <MenuItem value="Apple">Apple</MenuItem>
                      <MenuItem value="Samsung">Samsung</MenuItem>
                      <MenuItem value="LG">LG</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>시리즈</InputLabel>
                    <Select
                      value={formData.series}
                      label="시리즈"
                      onChange={(e) => setFormData({ ...formData, series: e.target.value })}
                    >
                      {getSeriesOptions().map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>연결성 옵션</InputLabel>
                    <Select
                      multiple
                      value={formData.connectivityOptions}
                      onChange={handleConnectivityChange}
                      input={<OutlinedInput label="연결성 옵션" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => <Chip key={value} label={value} size="small" />)}
                        </Box>
                      )}
                      disabled={deviceType === 'smartphone' || deviceType === 'wearable'}
                    >
                      {getConnectivityOptions().map((name) => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="모델명 (한글)"
                    value={formData.modelKo}
                    onChange={(e) => setFormData({ ...formData, modelKo: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="모델명 (영문)"
                    value={formData.modelEn}
                    onChange={(e) => setFormData({ ...formData, modelEn: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* 🆕 섹션 2: 용량(Variant)/구성 관리 */}
          {(
            <Grid item xs={12}>
              <Accordion 
                expanded={storageAccordionExpanded} 
                onChange={() => setStorageAccordionExpanded(!storageAccordionExpanded)}
                sx={{ 
                  border: '2px solid #6366f1',
                  borderRadius: 2,
                  '&:before': { display: 'none' },
                  boxShadow: storageAccordionExpanded ? '0 4px 12px rgba(99, 102, 241, 0.2)' : 'none'
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ 
                    bgcolor: '#f0f9ff',
                    borderRadius: storageAccordionExpanded ? '8px 8px 0 0' : 2
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <StorageIcon color="primary" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700} color="primary">
                        💾 용량(Variant) 관리
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        현재 {formData.storageOptions.length}개 용량 |
                        {newStorages.length > 0 && <Chip label={`+${newStorages.length} 추가 예정`} size="small" color="success" sx={{ ml: 1, height: 20 }} />}
                        {storagesToDelete.length > 0 && <Chip label={`-${storagesToDelete.length} 삭제 예정`} size="small" color="error" sx={{ ml: 1, height: 20 }} />}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: '#ffffff', p: 3 }}>
                  {/* 현재 용량 목록 */}
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    📋 현재 등록된 용량
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {formData.storageOptions.map((storage) => {
                        const isMarkedForDelete = storagesToDelete.includes(storage);
                        return (
                          <Chip
                            key={storage}
                            label={storage}
                            color={isMarkedForDelete ? 'error' : 'primary'}
                            variant={isMarkedForDelete ? 'outlined' : 'filled'}
                            sx={{
                              textDecoration: isMarkedForDelete ? 'line-through' : 'none',
                              opacity: isMarkedForDelete ? 0.6 : 1
                            }}
                            onDelete={() => handleToggleDeleteStorage(storage)}
                            deleteIcon={
                              <Tooltip title={isMarkedForDelete ? "삭제 취소 (복원)" : "삭제 예정에 추가"}>
                                {isMarkedForDelete ? <RestoreIcon /> : <DeleteIcon />}
                              </Tooltip>
                            }
                          />
                        );
                      })}
                      {formData.storageOptions.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          등록된 용량이 없습니다.
                        </Typography>
                      )}
                    </Box>
                    {storagesToDelete.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>{storagesToDelete.length}개 용량</strong>이 삭제 예정입니다. 
                          저장 시 해당 용량의 가격 행이 숨겨집니다. (복원 가능)
                        </Typography>
                      </Alert>
                    )}
                  </Paper>

                  {/* 새 용량 추가 섹션 */}
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    ➕ 새 용량 추가
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fffbeb', border: '1px dashed #f59e0b' }}>
                    <Grid container spacing={2} alignItems="flex-end">
                      {/* 용량 선택/입력 */}
                      <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>용량 선택</InputLabel>
                          <Select
                            value={newStorageInput}
                            label="용량 선택"
                            onChange={(e) => setNewStorageInput(e.target.value)}
                          >
                            {getDefaultStorageOptions()
                              .filter(opt => 
                                !formData.storageOptions.includes(opt) && 
                                !newStorages.some(ns => ns.storage === opt)
                              )
                              .map((opt) => (
                                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                              ))}
                            <Divider />
                            <MenuItem value="" disabled>
                              <em>직접 입력하려면 아래 필드 사용</em>
                            </MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          fullWidth
                          size="small"
                          label="또는 직접 입력"
                          placeholder="예: 512GB"
                          value={newStorageInput}
                          onChange={(e) => setNewStorageInput(e.target.value)}
                          sx={{ mt: 1 }}
                        />
                      </Grid>

                      {/* 초기 가격 설정 */}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                          초기 가격 (선택사항 - 나중에 설정 가능)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {(['A', 'B', 'C', 'D'] as DeviceGrade[]).map((grade) => (
                            <TextField
                              key={grade}
                              size="small"
                              label={grade}
                              value={newStoragePrices[grade] || ''}
                              onChange={(e) => handleNewStoragePriceChange(grade, e.target.value)}
                              sx={{ width: 80 }}
                              InputProps={{
                                sx: { fontSize: '0.8rem' }
                              }}
                            />
                          ))}
                        </Box>
                      </Grid>

                      {/* 추가 버튼 */}
                      <Grid item xs={12} sm={2}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="success"
                          startIcon={<AddIcon />}
                          onClick={handleAddNewStorage}
                          disabled={!newStorageInput.trim()}
                        >
                          추가
                        </Button>
                      </Grid>
                    </Grid>

                    {/* 추가 대기 목록 */}
                    {newStorages.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="success.main" fontWeight={600}>
                          ✅ 추가 대기 목록 ({newStorages.length}개)
                        </Typography>
                        <List dense sx={{ bgcolor: '#f0fdf4', borderRadius: 1, mt: 1 }}>
                          {newStorages.map((ns, index) => (
                            <ListItem key={ns.storage} divider={index < newStorages.length - 1}>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip label={ns.storage} color="success" size="small" />
                                    <Typography variant="caption" color="text.secondary">
                                      A: {ns.initialPrices.A.toLocaleString()}원 / 
                                      B: {ns.initialPrices.B.toLocaleString()}원 / 
                                      C: {ns.initialPrices.C.toLocaleString()}원 / 
                                      D: {ns.initialPrices.D.toLocaleString()}원
                                    </Typography>
                                  </Box>
                                }
                              />
                              <ListItemSecondaryAction>
                                <IconButton 
                                  edge="end" 
                                  size="small"
                                  onClick={() => handleRemoveNewStorage(ns.storage)}
                                  color="error"
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Paper>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>💡 팁:</strong> 용량 추가 시 각 연결성 옵션(Wi-Fi, Cellular 등)에 대해 자동으로 가격 행이 생성됩니다.
                      현재 연결성 옵션: <strong>{formData.connectivityOptions.join(', ')}</strong>
                    </Typography>
                  </Alert>
                </AccordionDetails>
              </Accordion>
            </Grid>
          )}

          {/* 섹션 3: 가격 정보 (타겟 구성이 있을 때만 표시) */}
          {targetConfig && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="secondary" gutterBottom sx={{ mt: 1 }}>
                💰 매입 가격 ({targetConfig.storage} / {targetConfig.connectivity})
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#ffffff', border: '1px solid #e2e8f0' }}>
                <Grid container spacing={2}>
                  {(['A', 'B', 'C', 'D'] as DeviceGrade[]).map((grade) => (
                    <Grid item xs={6} sm={3} key={grade}>
                      <TextField
                        fullWidth
                        label={getGradeLabel(grade)}
                        value={formData.prices[grade]}
                        onChange={(e) => handlePriceChange(grade, e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">원</InputAdornment>,
                        }}
                        sx={{ 
                          '& .MuiInputLabel-root': { 
                            color: grade === 'A' ? '#059669' : grade === 'D' ? '#ef4444' : 'inherit',
                            fontWeight: 'bold'
                          }
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* 변경사항 요약 */}
          {changesSummary.length > 0 && (
            <Grid item xs={12}>
              <Alert 
                severity="warning" 
                icon={<WarningIcon />}
                sx={{ 
                  bgcolor: '#fffbeb',
                  border: '1px solid #f59e0b'
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  📋 변경사항 요약 ({changesSummary.length}개)
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {changesSummary.map((change, index) => (
                    <li key={index}>
                      <Typography variant="body2">{change}</Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            </Grid>
          )}

          {/* 안내 메시지 */}
          <Grid item xs={12}>
            <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
              <strong>참고:</strong> 모델명, 시리즈, 연결성 옵션 수정은 모델 전체에 반영됩니다.
              {targetConfig && <><br/>가격은 선택하신 <strong>{targetConfig.storage} {targetConfig.connectivity}</strong> 구성에만 적용됩니다.</>}
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8fafc' }}>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          취소
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          disabled={loading || changesSummary.length === 0}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
        >
          {loading ? '저장 중...' : `모두 저장 (${changesSummary.length}개 변경)`}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default EditModelDialog;
